<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { isEqual } from 'lodash';

import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { i18n } from '@apps/main/modules/localization/localizationSetup';
import MoneyFormatter from '@apps/main/new-desktop/core/components/formatters/moneyFormatters/MoneyFormatter.vue';
import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import { AppButton } from '@libs/ui';

import { useActivityDetailsTicketsStore } from '../../stores/activityDetailsTicketsStore';

interface ActivityDetailsFooterEmits {
  (event: 'close'): void;
  (event: 'select-tickets'): void;
}
const emit = defineEmits<ActivityDetailsFooterEmits>();

const activityDetailsTicketsStore = useActivityDetailsTicketsStore();
const recipeStore = useRecipeStore();
const browserStore = useBrowserStore();
const { t } = i18n.global;

function handleSelect(): void {
  activityDetailsTicketsStore.selectTicket();
  emit('select-tickets');
}

const initialTickets = ref<
  Array<{ code: string; ageRangeId: string; quantity: number }>
>([]);

const currentTicketQuantity = computed<number>(() =>
  recipeStore.selectedActivityTickets.reduce(
    (sum, ticket) => sum + ticket.quantity,
    0
  )
);

const hasSameNumberOfTravelers = computed<boolean>(() =>
  recipeStore.selectedActivityTickets.some(
    (ticket) => ticket.getQuantityFromPax
  )
);

const hasTicketsChanged = computed<boolean>(() => {
  const currentTickets = recipeStore.selectedActivityTickets.map((ticket) => ({
    code: ticket.code,
    ageRangeId: ticket.ageRangeId,
    quantity: ticket.quantity,
    dateOffset: ticket.dateOffset,
    timeSlotId: ticket.timeSlotId,
  }));

  return !isEqual(initialTickets.value, currentTickets);
});

const headerLabel = computed<string>(() => {
  if (currentTicketQuantity.value > 0) {
    return `${currentTicketQuantity.value} ${t(
      'planner.tickets_selected',
      currentTicketQuantity.value
    )}`;
  }
  return t('planner.no_tickets_selected');
});

onMounted(() => {
  initialTickets.value = recipeStore.selectedActivityTickets.map((ticket) => ({
    code: ticket.code,
    ageRangeId: ticket.ageRangeId,
    quantity: ticket.quantity,
    dateOffset: ticket.dateOffset,
    timeSlotId: ticket.timeSlotId,
  }));
});
</script>

<template>
  <!--TODO: Duplicated component we should remove the old Ticket after release new activity detail page-->
  <footer class="activity-details-footer">
    <div class="payment-summary">
      <div class="total-price">
        <span class="total-price-header">
          {{ headerLabel }}
        </span>
        <MoneyFormatter
          v-if="
            !hasSameNumberOfTravelers &&
            activityDetailsTicketsStore.selectedActivityTotalPrice
          "
          class="total-price-price"
          :data="{
            price:
              activityDetailsTicketsStore.selectedActivityTotalPrice.amount,
            currency:
              activityDetailsTicketsStore.selectedActivityTotalPrice.currency,
            minPrice: undefined,
          }"
        />
      </div>
    </div>
    <div class="button-wrapper">
      <AppButton
        v-if="browserStore.isLargerThan1199px"
        class="select-activities-button"
        state="enabled"
        size="medium"
        type="modern"
        @click="emit('close')"
      >
        <span>
          {{ $t('planner.close') }}
        </span>
      </AppButton>
      <AppButton
        v-if="initialTickets.length > 0"
        class="select-activities-button"
        :state="hasTicketsChanged ? 'enabled' : 'disabled'"
        size="medium"
        type="primary"
        @click="handleSelect()"
      >
        <span>
          {{ $t('planner.update') }}
        </span>
      </AppButton>
      <AppButton
        v-else
        class="select-activities-button"
        :state="currentTicketQuantity > 0 ? 'enabled' : 'disabled'"
        size="medium"
        type="primary"
        @click="handleSelect()"
      >
        <span>
          {{ $t('planner.select') }}
        </span>
      </AppButton>
    </div>
  </footer>
</template>

<style lang="scss" scoped>
.activity-details-footer {
  padding: var(--spacing-24);
  display: flex;
  z-index: 1;
  align-items: center;
  justify-content: space-between;
  @include for-less-or-equal-than-900-px {
    padding: var(--spacing-16) var(--spacing-24);
  }
  @include for-more-or-equal-than-1200-px {
    border-radius: 0 0 var(--border-radius-12) var(--border-radius-12);
  }

  .payment-summary {
    margin-right: var(--spacing-24);
    text-align: right;
    display: flex;

    .total-price {
      display: flex;
      flex-direction: column;
      justify-content: center;

      .total-price-header {
        color: var(--color-neutral-70);
        @include font-label-m-light;
      }

      .total-price-price {
        color: var(--color-neutral-70);
        @include font-heading-3;
      }
    }
  }

  .payment-summary {
    text-align: left;
  }
  .button-wrapper {
    display: flex;
    gap: var(--spacing-24);

    .select-activities-button {
      min-width: 125px;
      margin: var(--spacing-2) 0;
    }
  }
}
</style>
